import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking");
  const [paymentDetails, setPaymentDetails] = useState(null);
  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (!sessionId) {
      navigate("/");
      return;
    }

    let attempts = 0;
    const maxAttempts = 5;
    const pollInterval = 2000;

    const checkPaymentStatus = async () => {
      if (attempts >= maxAttempts) {
        setStatus("timeout");
        return;
      }

      try {
        const response = await axios.get(`${API}/checkout/status/${sessionId}`);
        setPaymentDetails(response.data);

        if (response.data.payment_status === "paid") {
          setStatus("success");
          return;
        } else if (response.data.status === "expired") {
          setStatus("expired");
          return;
        }

        attempts++;
        setTimeout(checkPaymentStatus, pollInterval);
      } catch (error) {
        console.error("Error checking payment status:", error);
        setStatus("error");
      }
    };

    checkPaymentStatus();
  }, [sessionId, navigate]);

  if (status === "checking") {
    return (
      <div 
        data-testid="payment-checking"
        className="min-h-screen flex items-center justify-center bg-gradient-to-b from-white to-slate-50"
      >
        <div className="text-center space-y-6 p-8">
          <Loader2 className="w-16 h-16 animate-spin text-primary mx-auto" />
          <h2 className="text-2xl font-semibold text-primary">
            決済状況を確認中...
          </h2>
          <p className="text-slate-600">
            しばらくお待ちください
          </p>
        </div>
      </div>
    );
  }

  if (status === "success") {
    return (
      <div 
        data-testid="payment-success"
        className="min-h-screen flex items-center justify-center bg-gradient-to-b from-white to-slate-50 px-6"
      >
        <div className="max-w-2xl w-full bg-white rounded-2xl shadow-[0_12px_24px_rgba(0,0,0,0.06)] p-8 md:p-12 text-center space-y-8">
          <div className="flex justify-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
          </div>

          <div className="space-y-4">
            <h1 className="text-3xl md:text-4xl font-bold text-primary">
              お申し込みありがとうございます！
            </h1>
            <p className="text-base leading-relaxed text-slate-600">
              お支払いが正常に完了しました。
              <br />
              ご登録のメールアドレスに確認メールをお送りしました。
            </p>
          </div>

          {paymentDetails && (
            <div 
              data-testid="payment-details"
              className="bg-slate-50 rounded-lg p-6 text-left space-y-3"
            >
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-slate-700">プラン</span>
                <span className="text-sm text-slate-600">
                  {paymentDetails.metadata?.package_name || "—"}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-slate-700">金額</span>
                <span className="text-sm font-semibold text-primary">
                  ¥{paymentDetails.amount_total?.toLocaleString() || "—"}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-slate-700">ステータス</span>
                <span className="text-sm text-green-600 font-medium">
                  完了
                </span>
              </div>
            </div>
          )}

          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              担当者より3営業日以内にコンサルティングの開始についてご連絡させていただきます。
            </p>
            <Button
              data-testid="back-to-home-button"
              onClick={() => navigate("/")}
              className="w-full md:w-auto rounded-full bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-6 text-base font-medium transition-all duration-200 hover:scale-[1.02]"
            >
              トップページに戻る
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      data-testid="payment-error"
      className="min-h-screen flex items-center justify-center bg-gradient-to-b from-white to-slate-50 px-6"
    >
      <div className="max-w-2xl w-full bg-white rounded-2xl shadow-[0_12px_24px_rgba(0,0,0,0.06)] p-8 md:p-12 text-center space-y-8">
        <div className="space-y-4">
          <h1 className="text-3xl md:text-4xl font-bold text-primary">
            エラーが発生しました
          </h1>
          <p className="text-base leading-relaxed text-slate-600">
            決済の確認中に問題が発生しました。
            <br />
            お手数ですが、お問い合わせフォームよりご連絡ください。
          </p>
        </div>

        <Button
          data-testid="error-back-button"
          onClick={() => navigate("/")}
          className="w-full md:w-auto rounded-full bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-6 text-base font-medium transition-all duration-200 hover:scale-[1.02]"
        >
          トップページに戻る
        </Button>
      </div>
    </div>
  );
}
